import React from "react";
import {
  Search,
  X,
  CheckCircle2,
  Euro,
  Gift,
  Save,
  RotateCcw,
  Lock,
  KeyRound,
  AlertCircle,
  Upload,
  Building2,
  MapPin,
  LoaderCircle,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth0 } from "@auth0/auth0-react";
import "./OssAccountPage.css";
import { useOutletContext, useLocation, useNavigate } from "react-router-dom";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

let googleMapsLoadPromise = null;

const OSS_ACCOUNT_FORM_STORAGE_KEY = "legup_oss_account_form_draft";
const OSS_ACCOUNT_SERVICES_STORAGE_KEY = "legup_oss_account_services_draft";

const COUNTRY_LABELS = {
  BE: "Belgium",
  ES: "Spain",
};

const MAX_LOGO_SIZE_MB = 12;

const ACCEPTED_LOGO_TYPES = [
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/webp",
  "image/svg+xml",
];

function normalise(value = "") {
  return value.toLowerCase().trim();
}

function mapProfileServicesToSelections(profileServices = []) {
  const next = {};

  profileServices.forEach((item) => {
    next[item.service_code] = {
      service_code: item.service_code,
      label: item.service_label || item.service_code,
      description: item.service_description || "",
      service_type: item.service_type || "",
      subsidy_status:
        item.service_type === "PAID"
          ? ["YES", "NO"].includes(item.subsidy_status)
            ? item.subsidy_status
            : "NO"
          : "NOT_APPLICABLE",
      approximate_cost_text: item.approximate_cost_text || "",
      subsidy_information_url: item.subsidy_information_url || "",
      subsidy_information_label: item.subsidy_information_label || "",
      subsidy_notes: item.subsidy_notes || "",
    };
  });

  return next;
}

function createDefaultSelection(service, language = "en") {
  return {
    service_code: service.code,
    label:
      service[`label_${language}`] ||
      service.label_en ||
      service.label ||
      service.code,
    description:
      service[`description_${language}`] ||
      service.description_en ||
      service.description ||
      "",
    service_type: "",
    subsidy_status: "NOT_APPLICABLE",
    approximate_cost_text: "",
    subsidy_information_url: "",
    subsidy_information_label: "",
    subsidy_notes: "",
  };
}

function getServiceTitle(service, language) {
  return (
    service[`label_${language}`] ||
    service.label_en ||
    service.label ||
    service.service_label ||
    service.code
  );
}

function getServiceDescription(service, language) {
  return (
    service[`description_${language}`] ||
    service.description_en ||
    service.description ||
    ""
  );
}

function formatEuroNumber(value = "") {
  const clean = String(value).replace(/[^\d.]/g, "");
  if (!clean) return "";

  const [integerPart, decimalPart] = clean.split(".");
  const formattedInteger = Number(integerPart || 0).toLocaleString("en-US");

  if (decimalPart !== undefined) {
    return `${formattedInteger}.${decimalPart.slice(0, 2)}`;
  }

  return formattedInteger;
}

function readStorage(key, fallback) {
  try {
    const saved = sessionStorage.getItem(key);
    return saved ? JSON.parse(saved) : fallback;
  } catch {
    return fallback;
  }
}

function writeStorage(key, value) {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error(`Failed to save ${key}:`, error);
  }
}

function clearStorage(key) {
  try {
    sessionStorage.removeItem(key);
  } catch (error) {
    console.error(`Failed to clear ${key}:`, error);
  }
}

function getPhonePrefix(countryCode) {
  if (countryCode === "BE") return "+32";
  if (countryCode === "ES") return "+34";
  return "";
}

function getPhoneMaxDigits(countryCode) {
  if (countryCode === "BE") return 9;
  if (countryCode === "ES") return 9;
  return 15;
}

function splitPhoneByCountry(value = "", countryCode = "") {
  const prefix = getPhonePrefix(countryCode);
  const digits = String(value).replace(/\D/g, "");

  if (countryCode === "BE") {
    const local = digits.startsWith("32") ? digits.slice(2) : digits;
    return local.slice(0, getPhoneMaxDigits(countryCode));
  }

  if (countryCode === "ES") {
    const local = digits.startsWith("34") ? digits.slice(2) : digits;
    return local.slice(0, getPhoneMaxDigits(countryCode));
  }

  return digits.slice(0, getPhoneMaxDigits(countryCode));
}

function formatLocalPhone(value = "", countryCode = "") {
  const digits = String(value)
    .replace(/\D/g, "")
    .slice(0, getPhoneMaxDigits(countryCode));

  if (countryCode === "BE") {
    return [
      digits.slice(0, 3),
      digits.slice(3, 5),
      digits.slice(5, 7),
      digits.slice(7, 9),
    ]
      .filter(Boolean)
      .join(" ");
  }

  if (countryCode === "ES") {
    return [digits.slice(0, 3), digits.slice(3, 6), digits.slice(6, 9)]
      .filter(Boolean)
      .join(" ");
  }

  return digits;
}

function buildFullPhone(localValue = "", countryCode = "") {
  const prefix = getPhonePrefix(countryCode);
  const digits = String(localValue).replace(/\D/g, "");

  if (!prefix) return digits;

  return `${prefix} ${formatLocalPhone(digits, countryCode)}`.trim();
}

function stableStringify(value) {
  return JSON.stringify(value || {});
}

export default function OssAccountPage() {
  const { t, i18n } = useTranslation("oss-account");
  const { getAccessTokenSilently } = useAuth0();

  const language = React.useMemo(() => {
    const base = (i18n.resolvedLanguage || i18n.language || "en").split("-")[0];
    return ["en", "es", "ca", "nl"].includes(base) ? base : "en";
  }, [i18n.language, i18n.resolvedLanguage]);

  const [profile, setProfile] = React.useState(null);
  const [form, setForm] = React.useState({});
  const [serviceCatalog, setServiceCatalog] = React.useState([]);
  const [selections, setSelections] = React.useState({});
  const [search, setSearch] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [err, setErr] = React.useState("");
  const [success, setSuccess] = React.useState("");
  const [fieldErrors, setFieldErrors] = React.useState({});
  const location = useLocation();
  const [isSendingReset, setIsSendingReset] = React.useState(false);
  const [feedbackModal, setFeedbackModal] = React.useState({
    open: false,
    type: "",
    title: "",
    text: "",
  });

  const [addressSuggestions, setAddressSuggestions] = React.useState([]);

  const [addressSuggestionsOpen, setAddressSuggestionsOpen] =
    React.useState(false);

  const [addressSearching, setAddressSearching] = React.useState(false);

  const addressSearchTimerRef = React.useRef(null);

  const addressRequestIdRef = React.useRef(0);

  const addressSessionTokenRef = React.useRef(null);

  const placesLibraryPromiseRef = React.useRef(null);

  async function loadGoogleMapsApi() {
    if (window.google?.maps?.importLibrary) {
      return window.google.maps;
    }

    if (googleMapsLoadPromise) {
      return googleMapsLoadPromise;
    }

    if (!GOOGLE_MAPS_API_KEY) {
      throw new Error("REACT_APP_GOOGLE_MAPS_API_KEY is not configured.");
    }

    googleMapsLoadPromise = new Promise((resolve, reject) => {
      const existingScript = document.getElementById(
        "legup-google-maps-script",
      );

      if (existingScript) {
        existingScript.addEventListener("load", () => {
          if (window.google?.maps?.importLibrary) {
            resolve(window.google.maps);
          } else {
            reject(
              new Error("Google Maps loaded but importLibrary is unavailable."),
            );
          }
        });

        existingScript.addEventListener("error", () => {
          reject(new Error("Google Maps JavaScript API failed to load."));
        });

        return;
      }

      const script = document.createElement("script");

      script.id = "legup-google-maps-script";

      script.async = true;

      script.defer = true;

      script.src =
        "https://maps.googleapis.com/maps/api/js" +
        `?key=${encodeURIComponent(GOOGLE_MAPS_API_KEY)}` +
        "&libraries=places" +
        "&v=weekly";

      script.onload = () => {
        if (window.google?.maps?.importLibrary) {
          resolve(window.google.maps);
        } else {
          reject(
            new Error("Google Maps loaded but importLibrary is unavailable."),
          );
        }
      };

      script.onerror = () => {
        reject(new Error("Google Maps JavaScript API failed to load."));
      };

      document.head.appendChild(script);
    });

    return googleMapsLoadPromise;
  }

  async function getPlacesLibrary() {
    await loadGoogleMapsApi();

    if (!placesLibraryPromiseRef.current) {
      placesLibraryPromiseRef.current =
        window.google.maps.importLibrary("places");
    }

    return placesLibraryPromiseRef.current;
  }

  function handleCompanyAddressChange(value) {
    updateForm("company_address", value);

    if (addressSearchTimerRef.current) {
      window.clearTimeout(addressSearchTimerRef.current);
    }

    const query = value.trim();

    if (query.length < 3) {
      setAddressSuggestions([]);
      setAddressSuggestionsOpen(false);
      setAddressSearching(false);

      return;
    }

    setAddressSearching(true);
    setAddressSuggestionsOpen(true);

    const requestId = ++addressRequestIdRef.current;

    addressSearchTimerRef.current = window.setTimeout(async () => {
      try {
        const { AutocompleteSuggestion, AutocompleteSessionToken } =
          await getPlacesLibrary();

        if (!addressSessionTokenRef.current) {
          addressSessionTokenRef.current = new AutocompleteSessionToken();
        }

        const request = {
          input: query,

          sessionToken: addressSessionTokenRef.current,

          language: language || "en",
        };

        if (form.country_code) {
          request.includedRegionCodes = [form.country_code.toLowerCase()];

          request.region = form.country_code.toLowerCase();
        }

        const { suggestions } =
          await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

        if (requestId !== addressRequestIdRef.current) {
          return;
        }

        const predictions = (suggestions || [])
          .map((suggestion) => suggestion.placePrediction)
          .filter(Boolean);

        setAddressSuggestions(predictions);

        setAddressSuggestionsOpen(predictions.length > 0);
      } catch (error) {
        console.error("Google address autocomplete failed:", error);

        if (requestId === addressRequestIdRef.current) {
          setAddressSuggestions([]);
          setAddressSuggestionsOpen(false);
        }
      } finally {
        if (requestId === addressRequestIdRef.current) {
          setAddressSearching(false);
        }
      }
    }, 300);
  }

  async function handleCompanyAddressSelect(prediction) {
    if (!prediction) {
      return;
    }

    try {
      setAddressSearching(true);

      const place = prediction.toPlace();

      await place.fetchFields({
        fields: ["formattedAddress", "location"],
      });

      const selectedAddress =
        place.formattedAddress || prediction.text?.text || "";

      updateForm("company_address", selectedAddress);

      setAddressSuggestions([]);
      setAddressSuggestionsOpen(false);

      addressSessionTokenRef.current = null;
    } catch (error) {
      console.error("Could not select Google address:", error);
    } finally {
      setAddressSearching(false);
    }
  }

  React.useEffect(() => {
    return () => {
      if (addressSearchTimerRef.current) {
        window.clearTimeout(addressSearchTimerRef.current);
      }
    };
  }, []);

  const logoInputRef = React.useRef(null);

  const [logoFile, setLogoFile] = React.useState(null);
  const [logoPreview, setLogoPreview] = React.useState("");
  const [removeLogo, setRemoveLogo] = React.useState(false);
  const hasHydratedDraftRef = React.useRef(false);
  const [phoneLocal, setPhoneLocal] = React.useState("");
  const { refreshMe, setAccountDirty } = useOutletContext();
  const navigate = useNavigate();
  const [pendingNavigationPath, setPendingNavigationPath] = React.useState("");
  const [leaveModal, setLeaveModal] = React.useState(false);
  const [confirmModal, setConfirmModal] = React.useState({
    open: false,
    type: "",
    title: "",
    text: "",
    confirmLabel: "",
  });

  const [baseline, setBaseline] = React.useState({
    form: null,
    selections: null,
  });

  const dirty = React.useMemo(() => {
    if (!baseline.form || !baseline.selections) return false;

    return (
      stableStringify(form) !== stableStringify(baseline.form) ||
      stableStringify(selections) !== stableStringify(baseline.selections) ||
      Boolean(logoFile) ||
      Boolean(removeLogo)
    );
  }, [form, selections, baseline, logoFile, removeLogo]);

  React.useEffect(() => {
    setAccountDirty?.(Boolean(dirty));
  }, [dirty, setAccountDirty]);

  React.useEffect(() => {
    return () => {
      setAccountDirty?.(false);
    };
  }, [setAccountDirty]);

  React.useEffect(() => {
    function handleDocumentClick(event) {
      if (!dirty) return;

      const anchor = event.target.closest("a[href]");
      if (!anchor) return;

      const href = anchor.getAttribute("href");
      if (!href || href.startsWith("http") || href.startsWith("mailto:"))
        return;

      const nextPath = new URL(anchor.href).pathname;

      if (location.pathname === "/oss/account" && nextPath !== "/oss/account") {
        event.preventDefault();
        event.stopPropagation();

        setPendingNavigationPath(nextPath);
        setLeaveModal(true);
      }
    }

    document.addEventListener("click", handleDocumentClick, true);

    return () => {
      document.removeEventListener("click", handleDocumentClick, true);
    };
  }, [dirty, location.pathname]);

  function cancelLeavePage() {
    setLeaveModal(false);
    setPendingNavigationPath("");
  }

  function confirmLeavePage() {
    clearStorage(OSS_ACCOUNT_FORM_STORAGE_KEY);
    clearStorage(OSS_ACCOUNT_SERVICES_STORAGE_KEY);

    setLeaveModal(false);

    if (pendingNavigationPath) {
      navigate(pendingNavigationPath);
    }

    setPendingNavigationPath("");
  }

  function closeConfirmModal() {
    setConfirmModal({
      open: false,
      type: "",
      title: "",
      text: "",
      confirmLabel: "",
    });
  }

  function requestPasswordResetConfirmation() {
    setConfirmModal({
      open: true,
      type: "PASSWORD_RESET",
      title: t("confirm.passwordResetTitle"),
      text: t("confirm.passwordResetText", {
        email: form.contact_email,
      }),
      confirmLabel: t("confirm.sendEmail"),
    });
  }

  function requestDiscardConfirmation() {
    setConfirmModal({
      open: true,
      type: "DISCARD_CHANGES",
      title: t("confirm.discardTitle"),
      text: t("confirm.discardText"),
      confirmLabel: t("confirm.discard"),
    });
  }

  async function handleConfirmAction() {
    if (confirmModal.type === "PASSWORD_RESET") {
      closeConfirmModal();
      await handlePasswordReset();
      return;
    }

    if (confirmModal.type === "DISCARD_CHANGES") {
      closeConfirmModal();
      resetFromLoadedProfile();
    }
  }

  React.useEffect(() => {
    if (!(logoFile instanceof File)) return undefined;

    const objectUrl = URL.createObjectURL(logoFile);
    setLogoPreview(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [logoFile]);

  React.useEffect(() => {
    if (feedbackModal.open) {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: "smooth",
      });
    }
  }, [feedbackModal.open]);

  async function authHeaders() {
    const token = await getAccessTokenSilently();
    return {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };
  }

  React.useEffect(() => {
    async function loadData() {
      setLoading(true);
      setErr("");

      try {
        const headers = await authHeaders();

        const [profileRes, catalogRes] = await Promise.all([
          fetch(`${API_BASE}/api/oss/me/`, { headers }),
          fetch(`${API_BASE}/api/oss/service-catalog/`, { headers }),
        ]);

        const profileData = await profileRes.json().catch(() => ({}));
        const catalogData = await catalogRes.json().catch(() => ({}));

        if (!profileRes.ok) {
          throw new Error(profileData?.detail || t("messages.profileFailed"));
        }

        if (!catalogRes.ok) {
          throw new Error(catalogData?.detail || t("messages.catalogFailed"));
        }

        const loadedProfile = profileData.profile || {};
        const loadedAccount = profileData.account || {};

        setProfile(profileData);

        const backendForm = {
          organisation_name:
            loadedProfile.organisation_name ||
            loadedAccount.organisation_name ||
            "",

          contact_email:
            loadedProfile.contact_email || loadedAccount.email || "",
          country_code: loadedProfile.country_code || "",

          market_regions: Array.isArray(loadedProfile.market_regions)
            ? loadedProfile.market_regions
            : [],

          coverage_postal_codes: Array.isArray(loadedProfile.regions)
            ? loadedProfile.regions
            : [],

          vat_number: loadedProfile.vat_number || "",
          company_address: loadedProfile.company_address || "",
          contact_phone: loadedProfile.contact_phone || "",
          website_url: loadedProfile.website_url || "",
          role_in_company: loadedProfile.role_in_company || "",
          years_of_experience: loadedProfile.years_of_experience || "",
          short_description: loadedProfile.short_description || "",
          public_profile_summary: loadedProfile.public_profile_summary || "",
          additional_notes: loadedProfile.additional_notes || "",
          is_advisory: Boolean(loadedProfile.is_advisory),
          is_turnkey_renovation: Boolean(loadedProfile.is_turnkey_renovation),
        };

        setPhoneLocal(
          formatLocalPhone(
            splitPhoneByCountry(
              backendForm.contact_phone,
              backendForm.country_code,
            ),
            backendForm.country_code,
          ),
        );

        const backendSelections = mapProfileServicesToSelections(
          loadedProfile.services,
        );

        const savedForm = readStorage(OSS_ACCOUNT_FORM_STORAGE_KEY, null);

        const savedSelections = readStorage(
          OSS_ACCOUNT_SERVICES_STORAGE_KEY,
          null,
        );

        const hydratedForm = savedForm
          ? {
              ...backendForm,
              ...savedForm,

              organisation_name: backendForm.organisation_name,
              contact_email: backendForm.contact_email,
              country_code: backendForm.country_code,
              market_regions: backendForm.market_regions,
              vat_number: backendForm.vat_number,
              coverage_postal_codes: backendForm.coverage_postal_codes,
            }
          : backendForm;

        setBaseline({
          form: backendForm,
          selections: backendSelections,
        });

        setForm(hydratedForm);

        setPhoneLocal(
          formatLocalPhone(
            splitPhoneByCountry(
              hydratedForm.contact_phone,
              hydratedForm.country_code,
            ),
            hydratedForm.country_code,
          ),
        );

        setSelections(savedSelections || backendSelections);
        setAccountDirty?.(false);

        hasHydratedDraftRef.current = true;
        const catalogItems = Array.isArray(catalogData)
          ? catalogData
          : catalogData.results || [];

        setServiceCatalog(catalogItems);
      } catch (error) {
        setErr(error?.message || String(error));
      } finally {
        setLoading(false);
      }
    }

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredServices = React.useMemo(() => {
    const term = normalise(search);
    if (!term) return serviceCatalog;

    return serviceCatalog.filter((service) => {
      const title = getServiceTitle(service, language);
      const description = getServiceDescription(service, language);
      const category = service.category || "";
      return normalise(`${title} ${description} ${category}`).includes(term);
    });
  }, [serviceCatalog, search, language]);

  const paidSelections = React.useMemo(
    () =>
      Object.values(selections).filter((item) => item.service_type === "PAID"),
    [selections],
  );

  const nonPaidSelections = React.useMemo(
    () =>
      Object.values(selections).filter(
        (item) => item.service_type === "NON_PAID",
      ),
    [selections],
  );

  React.useEffect(() => {
    if (!hasHydratedDraftRef.current || loading) return;
    writeStorage(OSS_ACCOUNT_FORM_STORAGE_KEY, form);
  }, [form, loading]);

  React.useEffect(() => {
    if (!hasHydratedDraftRef.current || loading) return;
    writeStorage(OSS_ACCOUNT_SERVICES_STORAGE_KEY, selections);
  }, [selections, loading]);

  function triggerLogoPicker() {
    logoInputRef.current?.click();
  }

  function handleRemoveLogo() {
    setLogoFile(null);
    setLogoPreview("");
    setRemoveLogo(true);

    if (logoInputRef.current) {
      logoInputRef.current.value = "";
    }
  }

  function handleLogoChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ACCEPTED_LOGO_TYPES.includes(file.type)) {
      setFeedbackModal({
        open: true,
        type: "error",
        title: t("messages.errorTitle"),
        text: t("messages.logoTypeError"),
      });
      e.target.value = "";
      return;
    }

    const maxBytes = MAX_LOGO_SIZE_MB * 1024 * 1024;

    if (file.size > maxBytes) {
      setFeedbackModal({
        open: true,
        type: "error",
        title: t("messages.errorTitle"),
        text: t("messages.logoSizeError"),
      });
      e.target.value = "";
      return;
    }

    setLogoFile(file);
    setLogoPreview("");
    setRemoveLogo(false);
  }

  function updateForm(field, value) {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
    setSuccess("");
  }

  function getSelection(service) {
    return (
      selections[service.code] || createDefaultSelection(service, language)
    );
  }

  function setServiceType(service, serviceType) {
    setSelections((prev) => {
      const current =
        prev[service.code] || createDefaultSelection(service, language);

      if (current.service_type === serviceType) {
        const next = { ...prev };
        delete next[service.code];
        return next;
      }

      return {
        ...prev,
        [service.code]: {
          ...current,
          label: getServiceTitle(service, language),
          description: getServiceDescription(service, language),
          service_type: serviceType,
          subsidy_status:
            serviceType === "PAID"
              ? ["YES", "NO"].includes(current.subsidy_status)
                ? current.subsidy_status
                : ""
              : "NOT_APPLICABLE",
          approximate_cost_text:
            serviceType === "PAID" ? current.approximate_cost_text : "",
          subsidy_information_url:
            serviceType === "PAID" ? current.subsidy_information_url : "",
          subsidy_information_label:
            serviceType === "PAID" ? current.subsidy_information_label : "",
          subsidy_notes: serviceType === "PAID" ? current.subsidy_notes : "",
        },
      };
    });

    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next[service.code];
      return next;
    });
    setSuccess("");
  }

  function updateSelectionField(serviceCode, field, value) {
    setSelections((prev) => {
      const current = prev[serviceCode];
      if (!current) return prev;

      return {
        ...prev,
        [serviceCode]: {
          ...current,
          [field]: value,
        },
      };
    });

    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next[serviceCode];
      return next;
    });

    setSuccess("");
  }

  function selectAllAs(type) {
    setSelections((prev) => {
      const next = { ...prev };

      filteredServices.forEach((service) => {
        const current =
          next[service.code] || createDefaultSelection(service, language);

        next[service.code] = {
          ...current,
          label: getServiceTitle(service, language),
          description: getServiceDescription(service, language),
          service_type: type,
          subsidy_status:
            type === "PAID"
              ? ["YES", "NO"].includes(current.subsidy_status)
                ? current.subsidy_status
                : ""
              : "NOT_APPLICABLE",
          approximate_cost_text:
            type === "PAID" ? current.approximate_cost_text : "",
          subsidy_information_url:
            type === "PAID" ? current.subsidy_information_url : "",
          subsidy_information_label:
            type === "PAID" ? current.subsidy_information_label : "",
          subsidy_notes: type === "PAID" ? current.subsidy_notes : "",
        };
      });

      return next;
    });
  }

  function clearType(type) {
    setSelections((prev) => {
      const next = { ...prev };

      Object.entries(next).forEach(([code, item]) => {
        if (item.service_type === type) delete next[code];
      });

      return next;
    });
  }

  function validate() {
    const errors = {};
    const selected = Object.values(selections).filter(
      (item) => item.service_type,
    );

    if (!selected.length) {
      errors.general = t("validation.oneServiceRequired");
    }

    selected.forEach((item) => {
      if (item.service_type === "PAID" && !item.subsidy_status) {
        errors[item.service_code] = t("validation.subsidyRequired");
      }

      if (item.service_type === "PAID" && !item.approximate_cost_text.trim()) {
        errors[item.service_code] = t("validation.costRequired");
      }

      if (
        item.service_type === "PAID" &&
        item.subsidy_information_url &&
        !/^https?:\/\/.+/i.test(item.subsidy_information_url)
      ) {
        errors[item.service_code] = t("validation.validUrl");
      }
    });

    return errors;
  }

  async function saveProfile(e) {
    e.preventDefault();

    const errors = validate();
    setFieldErrors(errors);

    if (Object.keys(errors).length > 0) {
      setFeedbackModal({
        open: true,
        type: "error",
        title: t("messages.errorTitle"),
        text: errors.general || t("messages.correctHighlighted"),
      });
      return;
    }

    setSaving(true);
    setErr("");
    setSuccess("");

    try {
      const token = await getAccessTokenSilently();

      const payload = new FormData();

      payload.append("company_address", form.company_address || "");
      payload.append("contact_phone", form.contact_phone || "");
      payload.append("website_url", form.website_url || "");
      payload.append("role_in_company", form.role_in_company || "");
      payload.append("years_of_experience", form.years_of_experience || "");
      payload.append("short_description", form.short_description || "");
      payload.append(
        "public_profile_summary",
        form.public_profile_summary || "",
      );
      payload.append("additional_notes", form.additional_notes || "");
      payload.append("is_advisory", form.is_advisory ? "true" : "false");
      payload.append(
        "is_turnkey_renovation",
        form.is_turnkey_renovation ? "true" : "false",
      );
      payload.append("remove_logo", removeLogo ? "true" : "false");

      payload.append(
        "services",
        JSON.stringify(
          Object.values(selections).filter((item) => item.service_type),
        ),
      );

      if (logoFile) {
        payload.append("logo", logoFile);
      }

      const res = await fetch(`${API_BASE}/api/oss/account/update/`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: payload,
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data?.detail || t("messages.saveFailed"));
      }

      setProfile(data);
      await refreshMe?.({ silent: true });
      setAccountDirty?.(false);
      clearStorage(OSS_ACCOUNT_FORM_STORAGE_KEY);
      clearStorage(OSS_ACCOUNT_SERVICES_STORAGE_KEY);
      setLogoFile(null);
      setLogoPreview("");
      setRemoveLogo(false);

      if (logoInputRef.current) {
        logoInputRef.current.value = "";
      }

      setFeedbackModal({
        open: true,
        type: "success",
        title: t("messages.updateSuccessTitle"),
        text: t("messages.saveSuccess"),
      });
    } catch (error) {
      setFeedbackModal({
        open: true,
        type: "error",
        title: t("messages.errorTitle"),
        text: error.message || t("messages.saveFailed"),
      });
    } finally {
      setSaving(false);
    }
  }

  function resetFromLoadedProfile() {
    const loadedProfile = profile?.profile || {};
    const loadedAccount = profile?.account || {};

    const nextForm = {
      organisation_name:
        loadedProfile.organisation_name ||
        loadedAccount.organisation_name ||
        "",

      contact_email: loadedProfile.contact_email || loadedAccount.email || "",

      country_code: loadedProfile.country_code || "",

      market_regions: Array.isArray(loadedProfile.market_regions)
        ? loadedProfile.market_regions
        : [],

      coverage_postal_codes: Array.isArray(loadedProfile.regions)
        ? loadedProfile.regions
        : [],

      vat_number: loadedProfile.vat_number || "",
      company_address: loadedProfile.company_address || "",
      contact_phone: loadedProfile.contact_phone || "",
      website_url: loadedProfile.website_url || "",
      role_in_company: loadedProfile.role_in_company || "",
      years_of_experience: loadedProfile.years_of_experience || "",
      short_description: loadedProfile.short_description || "",
      public_profile_summary: loadedProfile.public_profile_summary || "",
      additional_notes: loadedProfile.additional_notes || "",
      is_advisory: Boolean(loadedProfile.is_advisory),
      is_turnkey_renovation: Boolean(loadedProfile.is_turnkey_renovation),
    };

    setForm(nextForm);

    setPhoneLocal(
      formatLocalPhone(
        splitPhoneByCountry(nextForm.contact_phone, nextForm.country_code),
        nextForm.country_code,
      ),
    );

    setSelections(mapProfileServicesToSelections(loadedProfile.services));

    setErr("");
    setSuccess("");
    setFieldErrors({});

    setLogoFile(null);
    setLogoPreview("");
    setRemoveLogo(false);

    clearStorage(OSS_ACCOUNT_FORM_STORAGE_KEY);

    clearStorage(OSS_ACCOUNT_SERVICES_STORAGE_KEY);
  }

  function renderLockedInput(label, value) {
    return (
      <label className="oss-account-field">
        {label}
        <div className="oss-account-locked-input">
          <input value={value || ""} disabled />
          <Lock size={14} />
        </div>
      </label>
    );
  }

  function renderServiceCard(service, type) {
    const selected = getSelection(service);
    const isChecked = selected.service_type === type;
    const title = getServiceTitle(service, language);
    const description = getServiceDescription(service, language);
    const hasError = fieldErrors[service.code];

    return (
      <div
        key={`${type}-${service.code}`}
        className={
          isChecked ? "oss-account-service selected" : "oss-account-service"
        }
      >
        <label className="oss-account-service-check">
          <input
            type="checkbox"
            checked={isChecked}
            onChange={() => setServiceType(service, type)}
          />

          <span>
            <strong>{title}</strong>
            {description && <small>{description}</small>}
          </span>
        </label>

        {type === "PAID" && isChecked && (
          <div className="oss-account-paid-fields">
            <div className="oss-account-mini-grid">
              <label className="oss-account-field">
                <span className="oss-account-field-label-fixed">
                  {t("fields.subsidised")}
                </span>
                <select
                  value={selected.subsidy_status}
                  onChange={(e) =>
                    updateSelectionField(
                      service.code,
                      "subsidy_status",
                      e.target.value,
                    )
                  }
                >
                  <option value="">{t("common.select")}</option>
                  <option value="YES">{t("common.yes")}</option>
                  <option value="NO">{t("common.no")}</option>
                </select>
                <p className="oss-account-field-hint">
                  {t("fields.subsidisedHint")}
                </p>
              </label>

              <label className="oss-account-field">
                <span className="oss-account-field-label-fixed">
                  {t("fields.approximateCost")}
                </span>
                <div className="oss-account-euro-input">
                  <input
                    value={selected.approximate_cost_text}
                    onChange={(e) =>
                      updateSelectionField(
                        service.code,
                        "approximate_cost_text",
                        formatEuroNumber(e.target.value),
                      )
                    }
                    placeholder={t("placeholders.approximateCost")}
                    className={hasError ? "input-error" : ""}
                  />
                  <span>€</span>
                </div>
              </label>
            </div>

            {selected.subsidy_status === "YES" && (
              <div className="oss-account-subsidy-box">
                <label className="oss-account-field">
                  <span className="oss-account-field-label-fixed">
                    {t("fields.subsidyInformationUrl")}
                  </span>
                  <input
                    value={selected.subsidy_information_url}
                    onChange={(e) =>
                      updateSelectionField(
                        service.code,
                        "subsidy_information_url",
                        e.target.value,
                      )
                    }
                    placeholder={t("placeholders.subsidyInformationUrl")}
                    className={hasError ? "input-error" : ""}
                  />
                </label>

                <label className="oss-account-field">
                  <span className="oss-account-field-label-fixed">
                    {t("fields.subsidyInformationLabel")}
                  </span>
                  <input
                    value={selected.subsidy_information_label}
                    onChange={(e) =>
                      updateSelectionField(
                        service.code,
                        "subsidy_information_label",
                        e.target.value,
                      )
                    }
                    placeholder={t("placeholders.subsidyInformationLabel")}
                  />
                </label>

                <label className="oss-account-field full">
                  <span className="oss-account-field-label-fixed">
                    {t("fields.subsidyNotes")}
                  </span>
                  <textarea
                    rows="2"
                    value={selected.subsidy_notes}
                    onChange={(e) =>
                      updateSelectionField(
                        service.code,
                        "subsidy_notes",
                        e.target.value,
                      )
                    }
                    placeholder={t("placeholders.subsidyNotes")}
                  />
                </label>
              </div>
            )}

            {hasError && (
              <span className="oss-account-field-error">{hasError}</span>
            )}
          </div>
        )}
      </div>
    );
  }

  if (loading) {
    return (
      <main className="oss-account-page">
        <div className="oss-account-loading">{t("messages.loading")}</div>
      </main>
    );
  }

  async function handlePasswordReset() {
    try {
      setIsSendingReset(true);

      const token = await getAccessTokenSilently();

      const response = await fetch(`${API_BASE}/api/auth/password-reset/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data?.detail || t("messages.passwordResetError"));
      }

      setFeedbackModal({
        open: true,
        type: "success",
        title: t("messages.successTitle"),
        text: t("messages.passwordResetSent"),
      });
    } catch (error) {
      setFeedbackModal({
        open: true,
        type: "error",
        title: t("messages.errorTitle"),
        text: error.message || t("messages.passwordResetError"),
      });
    } finally {
      setIsSendingReset(false);
    }
  }

  return (
    <main className="oss-account-page">
      <section className="oss-account-hero">
        <p>{t("hero.kicker")}</p>
        <h1>{t("hero.title")}</h1>
        <span>{t("hero.subtitle")}</span>
        <span className="oss-account-unsaved-note">
          <AlertCircle size={22} />
          {t("messages.unsavedChangesNote")}
        </span>
      </section>

      <form className="oss-account-card" onSubmit={saveProfile}>
        {err && <div className="oss-account-message error">{err}</div>}
        {success && (
          <div className="oss-account-message success">{success}</div>
        )}

        <section className="oss-account-section">
          <h2>{t("sections.company")}</h2>

          <div className="oss-account-logo-block">
            <div className="oss-account-logo-preview">
              {(() => {
                const savedLogoUrl =
                  profile?.account?.image_url ||
                  profile?.profile?.logo_url ||
                  profile?.profile?.image_url ||
                  "";

                const resolvedLogoUrl =
                  savedLogoUrl && savedLogoUrl.startsWith("http")
                    ? savedLogoUrl
                    : savedLogoUrl
                      ? `${API_BASE}${savedLogoUrl}`
                      : "";

                const visibleLogoUrl = logoPreview || resolvedLogoUrl;

                return visibleLogoUrl && !removeLogo ? (
                  <img
                    src={visibleLogoUrl}
                    alt={t("logo.alt")}
                    className="oss-account-logo-image"
                  />
                ) : (
                  <div className="oss-account-logo-placeholder">
                    <Building2 size={28} />
                  </div>
                );
              })()}
            </div>

            <div className="oss-account-logo-actions">
              <div className="oss-account-logo-buttons">
                <button
                  type="button"
                  className="oss-account-upload-btn"
                  onClick={triggerLogoPicker}
                >
                  <Upload size={15} />
                  {t("logo.upload")}
                </button>

                <button
                  type="button"
                  className="oss-account-upload-btn oss-account-upload-btn--ghost"
                  onClick={handleRemoveLogo}
                >
                  <X size={15} />
                  {t("logo.remove")}
                </button>
              </div>

              <input
                ref={logoInputRef}
                type="file"
                accept=".png,.jpg,.jpeg,.webp,.svg"
                hidden
                onChange={handleLogoChange}
              />

              <p className="oss-account-hint">{t("logo.hint")}</p>
            </div>
          </div>

          <div
            className={
              addressSuggestionsOpen
                ? "oss-account-grid oss-account-grid--address-open"
                : "oss-account-grid"
            }
          >
            {renderLockedInput(
              t("fields.organisationName"),
              form.organisation_name,
            )}
            {renderLockedInput(t("fields.email"), form.contact_email)}
            {renderLockedInput(
              t("fields.country"),
              COUNTRY_LABELS[form.country_code] || form.country_code,
            )}

            <div className="oss-account-field">
              <span className="oss-account-field-label">
                {t("fields.marketRegions")}
              </span>

              <div className="oss-account-locked-input oss-account-market-regions">
                <div className="oss-account-market-regions__list">
                  {Array.isArray(form.market_regions) &&
                  form.market_regions.length ? (
                    form.market_regions.map((marketRegion) => (
                      <span
                        key={marketRegion.id || marketRegion.code}
                        className="oss-account-market-region-pill"
                      >
                        {marketRegion.name || marketRegion.code}
                      </span>
                    ))
                  ) : (
                    <span className="oss-account-market-regions__empty">—</span>
                  )}
                </div>

                <Lock size={14} />
              </div>
            </div>
            <div className="oss-account-field full">
              <span className="oss-account-field-label">
                {t("fields.coveragePostalCodes")}
              </span>

              <div className="oss-account-coverage-box">
                <div className="oss-account-coverage-pills">
                  {form.coverage_postal_codes?.length ? (
                    form.coverage_postal_codes.map((coverage) => (
                      <span
                        key={
                          coverage.id ||
                          `${coverage.country_code}-${coverage.postal_code}`
                        }
                        className="oss-account-coverage-pill"
                      >
                        {coverage.postal_code}
                      </span>
                    ))
                  ) : (
                    <span className="oss-account-coverage-empty">
                      {t("fields.noCoveragePostalCodes")}
                    </span>
                  )}
                </div>
                <Lock size={14} />
              </div>

              <p className="oss-account-field-hint">
                {t("fields.coverageManagedByAdmin")}
              </p>
            </div>
            {renderLockedInput(t("fields.vatNumber"), form.vat_number)}

            <label className="oss-account-field">
              {t("fields.role")}
              <input
                value={form.role_in_company}
                onChange={(e) => updateForm("role_in_company", e.target.value)}
              />
            </label>

            <label className="oss-account-field full">
              {t("fields.companyAddress")}

              <div className="oss-account-address-autocomplete">
                <div className="oss-account-address-input">
                  <MapPin size={17} />

                  <input
                    value={form.company_address || ""}
                    onChange={(e) => handleCompanyAddressChange(e.target.value)}
                    onFocus={() => {
                      if (addressSuggestions.length) {
                        setAddressSuggestionsOpen(true);
                      }
                    }}
                    onBlur={() => {
                      window.setTimeout(() => {
                        setAddressSuggestionsOpen(false);
                      }, 180);
                    }}
                    placeholder={t("fields.companyAddressPlaceholder", {
                      defaultValue: "Start typing an address...",
                    })}
                    autoComplete="off"
                  />

                  {addressSearching ? (
                    <LoaderCircle
                      size={16}
                      className="oss-account-address-spinner"
                    />
                  ) : null}
                </div>

                {addressSuggestionsOpen && addressSuggestions.length > 0 ? (
                  <div className="oss-account-address-suggestions">
                    {addressSuggestions.map((prediction) => {
                      const primary =
                        prediction.mainText?.text ||
                        prediction.text?.text ||
                        "";

                      const secondary = prediction.secondaryText?.text || "";

                      return (
                        <button
                          key={prediction.placeId || prediction.text?.text}
                          type="button"
                          className="oss-account-address-suggestion"
                          onMouseDown={(event) => {
                            event.preventDefault();

                            handleCompanyAddressSelect(prediction);
                          }}
                        >
                          <span className="oss-account-address-suggestion__icon">
                            <MapPin size={15} />
                          </span>

                          <span className="oss-account-address-suggestion__text">
                            <strong>{primary}</strong>

                            {secondary ? <small>{secondary}</small> : null}
                          </span>
                        </button>
                      );
                    })}

                    <div className="oss-account-address-google">
                      Powered by Google
                    </div>
                  </div>
                ) : null}
              </div>
            </label>

            <label className="oss-account-field">
              {t("fields.phone")}

              <div className="oss-account-phone-input">
                <span>{getPhonePrefix(form.country_code)}</span>

                <input
                  value={phoneLocal}
                  onChange={(e) => {
                    const formatted = formatLocalPhone(
                      e.target.value,
                      form.country_code,
                    );

                    setPhoneLocal(formatted);

                    updateForm(
                      "contact_phone",
                      buildFullPhone(formatted, form.country_code),
                    );
                  }}
                  placeholder={
                    form.country_code === "BE"
                      ? "470 12 34 56"
                      : form.country_code === "ES"
                        ? "612 345 678"
                        : t("fields.phonePlaceholder")
                  }
                  inputMode="numeric"
                  maxLength={form.country_code === "BE" ? 12 : 11}
                />
              </div>

              <p className="oss-account-field-hint">
                {form.country_code === "BE"
                  ? t("fields.phoneHintBE")
                  : form.country_code === "ES"
                    ? t("fields.phoneHintES")
                    : t("fields.phoneHintGeneric")}
              </p>
            </label>

            <label className="oss-account-field">
              {t("fields.website")}
              <input
                value={form.website_url}
                onChange={(e) => updateForm("website_url", e.target.value)}
              />
            </label>

            <label className="oss-account-field">
              {t("fields.experience")}
              <input
                type="number"
                min="0"
                value={form.years_of_experience}
                onChange={(e) =>
                  updateForm("years_of_experience", e.target.value)
                }
              />
            </label>
          </div>

          <div className="oss-account-field full">
            <label className="oss-account-field-label">
              {t("fields.ossTypes")}
            </label>

            <div className="oss-account-type-row">
              <label>
                <input
                  type="checkbox"
                  checked={form.is_advisory}
                  onChange={(e) => updateForm("is_advisory", e.target.checked)}
                />
                {t("fields.isAdvisory")}
              </label>

              <label>
                <input
                  type="checkbox"
                  checked={form.is_turnkey_renovation}
                  onChange={(e) =>
                    updateForm("is_turnkey_renovation", e.target.checked)
                  }
                />
                {t("fields.isTurnkey")}
              </label>
            </div>
          </div>
        </section>

        <section className="oss-account-section">
          <h2>{t("sections.security")}</h2>

          <div className="oss-account__security">
            <div className="oss-account__security-info">
              <div className="oss-account__security-icon">
                <KeyRound size={18} />
              </div>

              <div>
                <h3 className="oss-account__security-title">
                  {t("security.passwordTitle")}
                </h3>
                <p className="oss-account__security-text">
                  {t("security.passwordText")}
                </p>
              </div>
            </div>

            <button
              type="button"
              className="oss-account__reset-btn"
              onClick={requestPasswordResetConfirmation}
              disabled={isSendingReset}
            >
              {isSendingReset
                ? t("buttons.sending")
                : t("buttons.sendPasswordReset")}
            </button>
          </div>
        </section>

        <section className="oss-account-section">
          <h2>{t("sections.description")}</h2>

          <label className="oss-account-field">
            {t("fields.shortDescription")}
            <textarea
              rows="4"
              value={form.short_description}
              onChange={(e) => updateForm("short_description", e.target.value)}
            />
          </label>
        </section>

        <section className="oss-account-section">
          <div className="oss-account-services-top">
            <div>
              <h2>{t("sections.services")}</h2>
              <p>{t("sections.servicesHint")}</p>
            </div>

            <div className="oss-account-search">
              <Search size={17} />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("placeholders.search")}
              />
              {search && (
                <button type="button" onClick={() => setSearch("")}>
                  <X size={15} />
                </button>
              )}
            </div>
          </div>

          <div className="oss-account-summary-row">
            <div>
              <CheckCircle2 size={16} />
              {t("summary.selected", {
                count: paidSelections.length + nonPaidSelections.length,
              })}
            </div>
            <div>
              <Euro size={16} />
              {t("summary.paid", { count: paidSelections.length })}
            </div>
            <div>
              <Gift size={16} />
              {t("summary.nonPaid", { count: nonPaidSelections.length })}
            </div>
          </div>

          <div className="oss-account-service-columns">
            <section className="oss-account-service-column">
              <div className="oss-account-column-header">
                <div>
                  <h3>{t("columns.paid.title")}</h3>
                  <p>{t("columns.paid.subtitle")}</p>
                </div>

                <div>
                  <button type="button" onClick={() => clearType("PAID")}>
                    {t("buttons.clear")}
                  </button>
                  <button type="button" onClick={() => selectAllAs("PAID")}>
                    {t("buttons.selectAll")}
                  </button>
                </div>
              </div>

              <div className="oss-account-service-list">
                {filteredServices.map((service) =>
                  renderServiceCard(service, "PAID"),
                )}
              </div>
            </section>

            <section className="oss-account-service-column">
              <div className="oss-account-column-header">
                <div>
                  <h3>{t("columns.nonPaid.title")}</h3>
                  <p>{t("columns.nonPaid.subtitle")}</p>
                </div>

                <div>
                  <button type="button" onClick={() => clearType("NON_PAID")}>
                    {t("buttons.clear")}
                  </button>
                  <button type="button" onClick={() => selectAllAs("NON_PAID")}>
                    {t("buttons.selectAll")}
                  </button>
                </div>
              </div>

              <div className="oss-account-service-list">
                {filteredServices.map((service) =>
                  renderServiceCard(service, "NON_PAID"),
                )}
              </div>
            </section>
          </div>
        </section>

        <div className="oss-account-footer">
          <button
            type="button"
            className="oss-account-secondary"
            onClick={requestDiscardConfirmation}
          >
            <RotateCcw size={16} />
            {t("buttons.discard")}
          </button>

          <button
            type="submit"
            className="oss-account-primary"
            disabled={saving}
          >
            <Save size={16} />
            {saving ? t("buttons.saving") : t("buttons.save")}
          </button>
        </div>
      </form>
      {confirmModal.open ? (
        <div
          className="oss-account-confirm-overlay"
          onClick={closeConfirmModal}
        >
          <div
            className="oss-account-confirm"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="oss-account-confirm__icon">
              <AlertCircle size={22} />
            </div>

            <div className="oss-account-confirm__content">
              <h3>{confirmModal.title}</h3>
              <p>{confirmModal.text}</p>

              <div className="oss-account-confirm__actions">
                <button
                  type="button"
                  className="oss-account-confirm__cancel"
                  onClick={closeConfirmModal}
                >
                  {t("confirm.cancel")}
                </button>

                <button
                  type="button"
                  className="oss-account-confirm__confirm"
                  onClick={handleConfirmAction}
                >
                  {confirmModal.confirmLabel}
                </button>
              </div>
            </div>

            <button
              type="button"
              className="oss-account-confirm__close"
              onClick={closeConfirmModal}
              aria-label={t("common.close")}
            >
              <X size={16} />
            </button>
          </div>
        </div>
      ) : null}

      {leaveModal ? (
        <div className="oss-account-confirm-overlay" onClick={cancelLeavePage}>
          <div
            className="oss-account-confirm"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="oss-account-confirm__icon">
              <AlertCircle size={22} />
            </div>

            <div className="oss-account-confirm__content">
              <h3>{t("confirm.leaveTitle")}</h3>
              <p>{t("confirm.leaveText")}</p>

              <div className="oss-account-confirm__actions">
                <button
                  type="button"
                  className="oss-account-confirm__cancel"
                  onClick={cancelLeavePage}
                >
                  {t("confirm.stay")}
                </button>

                <button
                  type="button"
                  className="oss-account-confirm__confirm"
                  onClick={confirmLeavePage}
                >
                  {t("confirm.leave")}
                </button>
              </div>
            </div>

            <button
              type="button"
              className="oss-account-confirm__close"
              onClick={cancelLeavePage}
              aria-label={t("common.close")}
            >
              <X size={16} />
            </button>
          </div>
        </div>
      ) : null}

      {feedbackModal.open ? (
        <div
          className="oss-account-modal-overlay"
          onClick={() =>
            setFeedbackModal({ open: false, type: "", title: "", text: "" })
          }
        >
          <div
            className={`oss-account-modal ${
              feedbackModal.type === "error"
                ? "oss-account-modal--error"
                : "oss-account-modal--success"
            }`}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="oss-account-modal__icon">
              {feedbackModal.type === "error" ? (
                <AlertCircle size={22} />
              ) : (
                <CheckCircle2 size={22} />
              )}
            </div>

            <div className="oss-account-modal__content">
              <h3>{feedbackModal.title}</h3>
              <p>{feedbackModal.text}</p>
            </div>

            <button
              type="button"
              className="oss-account-modal__close"
              onClick={() =>
                setFeedbackModal({ open: false, type: "", title: "", text: "" })
              }
              aria-label={t("common.close")}
            >
              <X size={16} />
            </button>
          </div>
        </div>
      ) : null}
    </main>
  );
}
